import { useContext, useEffect, useState } from 'react';
import { PatternFormat } from 'react-number-format';
import AnimatedComponent from '../Components/AnimatedComponent';
import Button from '../Components/Button';
import MobileHeroDiagonal from '../Components/MobileHeroDiagonal';
import NavigationBar from '../Components/NavigationBar';
import SupportActionCards from '../Components/SupportActionCards';
import { LanguageContext } from '../Components/LanguageContext';
import handleTransformLang from '../functions/handleTransformLang';
import Layout from '../Layout';
import NavLang from '../lang/pages/nav.json';
import supportPage from '../lang/pages/support.json';
import Background from '../public/images/bg_main.svg';

const FEEDBACK_INPUT_CLASS =
  'block w-full border-0 bg-white px-4 py-3 text-[17px] text-text_secondary ring-1 ring-[#b8bec8] transition focus:outline-none focus:ring-2 focus:ring-text_primary placeholder:text-[#afb7c4]';

export default function Support() {
  return (
    <Layout headData={supportPage.head}>
      <InnerPage />
    </Layout>
  );
}

function InnerPage() {
  const { language } = useContext(LanguageContext);
  const [content, setContent] = useState(handleTransformLang(NavLang));
  const [pageContent, setPageContent] = useState(handleTransformLang(supportPage));

  useEffect(() => {
    setContent(handleTransformLang(NavLang));
    setPageContent(handleTransformLang(supportPage));
  }, [language]);

  return (
    <>
      <section
        className="relative overflow-hidden bg-white pb-12 sm:pb-16 1.5md:pb-20 xl:pb-24 1.5xl:pb-20"
      >
        <div
          className="absolute inset-0 hidden bg-no-repeat sm:block sm:bg-[length:1180px_480px] sm:bg-top 1.5md:bg-[length:1560px_620px] 1.5md:bg-top xl:bg-[length:1760px_700px] xl:bg-top 1.5xl:bg-[length:1915px_750px] 1.5xl:bg-top"
          style={{ backgroundImage: `url(${Background})` }}
        />
        <MobileHeroDiagonal className="top-[11.25rem] h-[16.5rem] w-[12.6rem] sm:top-[5rem]" />
        <div className="h-1.5 w-full bg-bg_secondary" />

        <div className="relative z-20 container mx-auto max-w-8xl px-5 py-5 sm:px-4 sm:py-7 1.5md:py-9 1.5xl:py-11">
          <NavigationBar
            content={content}
            desktopNavBreakpoint="1.5xl"
            logoClassName="h-18 sm:h-20 1.5md:h-22 1.5xl:h-25"
            b2bButtonClassName="px-4 py-[0.45rem] text-[13px] 1.5md:px-4 1.5md:py-[0.45rem] 1.5md:text-[14px] 1.5xl:px-5 1.5xl:py-2 1.5xl:text-lg"
            hamburgerIconClassName="h-9 w-9 sm:h-10 sm:w-10 1.5md:h-11 1.5md:w-11"
          />

          <div className="grid items-start gap-6 pt-2 sm:gap-8 sm:pt-4 1.5md:grid-cols-[0.67fr_1.05fr] 1.5md:gap-6 1.5md:pt-8 xl:grid-cols-[0.78fr_1.02fr] xl:pt-12 1.5xl:items-center 1.5xl:gap-10 1.5xl:pt-16">
            <AnimatedComponent
              direction="animate-fade-right"
              className="order-2 max-w-[20rem] sm:max-w-[23rem] 1.5md:order-1 1.5md:max-w-[20rem] 1.5md:pt-18 1.5md:ps-8 xl:max-w-[27rem] xl:pt-16 1.5xl:max-w-[31rem] 1.5xl:pt-20 1.5xl:ps-10"
            >
              <h1 className="text-[23px] font-bold leading-[1.12] text-text_primary sm:text-[25px] 1.5md:text-[28px] xl:text-[32px] 1.5xl:text-[36px]">
                {pageContent.hero.title}
              </h1>

              <p className="mt-3 max-w-[20rem] text-[13px] leading-[1.55] text-text_secondary sm:max-w-[23rem] sm:text-[14px] 1.5md:mt-4 1.5md:max-w-[18rem] 1.5md:text-[13.5px] xl:max-w-[23rem] xl:text-[15px] xl:leading-7 1.5xl:mt-5 1.5xl:max-w-[29rem] 1.5xl:text-[17px] 1.5xl:leading-8">
                {pageContent.hero.description}
              </p>

              <div className="mt-3 space-y-1.5 sm:space-y-2 1.5md:mt-4 1.5xl:mt-4 1.5xl:space-y-2.5">
                {pageContent.hero.items.map((item) => (
                  <div
                    key={item}
                    className="flex items-start gap-2 text-[13px] leading-[1.45] text-text_secondary sm:text-[13.5px] xl:text-[14px] xl:leading-6 1.5xl:gap-2.5 1.5xl:text-[16px] 1.5xl:leading-7"
                  >
                    <img
                      src="/images/support/check_mark.svg"
                      alt=""
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 sm:mt-1 sm:h-4 sm:w-4"
                    />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </AnimatedComponent>

            <AnimatedComponent
              direction="animate-fade-left"
              className="order-1 flex justify-center 1.5md:order-2 1.5md:justify-end 1.5md:ps-2"
            >
              <img
                src="/images/support/banner_image.svg"
                alt=""
                className="relative z-10 mx-auto mt-[1.875rem] w-full max-w-[20rem] translate-x-[0.35rem] object-contain sm:mt-[3.125rem] 1.5md:mt-0 1.5md:max-w-[22rem] 1.5md:translate-x-[10px] 1.5md:translate-y-[68px] xl:max-w-[28rem] xl:translate-x-[8px] xl:translate-y-[92px] 1.5xl:max-w-[34.5rem] 1.5xl:translate-x-[2px] 1.5xl:translate-y-[110px]"
              />
            </AnimatedComponent>
          </div>
        </div>
      </section>

      <section className="bg-white pb-18 pt-3 sm:pt-4 1.5md:pb-24 1.5md:pt-8 xl:pt-10 1.5xl:pb-28">
        <div className="container mx-auto max-w-8xl px-4">
          <AnimatedComponent
            direction="animate-fade-up animate-duration-500"
            className="mx-auto w-full max-w-[21.5rem] sm:max-w-[22rem] 1.5md:max-w-fit"
          >
            <SupportActionCards cards={pageContent.cards} />
          </AnimatedComponent>
        </div>
      </section>

      <section className="bg-[#f4f5f7] py-14 sm:py-16 1.5md:py-18 xl:py-20 1.5xl:py-24">
        <div className="container mx-auto max-w-8xl px-4">
          <AnimatedComponent
            direction="animate-fade-up animate-duration-500"
            className="mx-auto max-w-[73.25rem] xl:max-w-[78.25rem]"
          >
            <div className="text-center">
              <h2 className="text-[24px] font-bold leading-tight text-text_primary sm:text-[28px] 1.5md:text-[32px] 1.5xl:text-[36px]">
                {pageContent.feedback.title}
              </h2>
              <p className="mx-auto mt-3 max-w-[20rem] text-[13px] leading-[1.55] text-text_secondary sm:max-w-[28rem] sm:text-[14px] 1.5md:mt-4 1.5md:max-w-[58rem] 1.5md:text-[15px] xl:max-w-[62rem] xl:text-[16px] xl:leading-7 1.5xl:max-w-[64rem] 1.5xl:text-[17px] 1.5xl:leading-8">
                {pageContent.feedback.description}
              </p>
            </div>

            <form
              className="mx-auto mt-10 max-w-[67.25rem] sm:mt-12 xl:max-w-[72.25rem] 1.5md:mt-14 xl:mt-16"
              onSubmit={(event) => event.preventDefault()}
            >
              <div className="grid gap-x-10 gap-y-8 1.5md:gap-y-10 xl:gap-x-12 xl:gap-y-12 lg:grid-cols-2">
                <SupportField
                  label={pageContent.feedback.fields.companyName}
                  input={
                    <input
                      type="text"
                      maxLength={80}
                      className={FEEDBACK_INPUT_CLASS}
                    />
                  }
                />

                <SupportField
                  label={pageContent.feedback.fields.fullName}
                  input={
                    <input
                      type="text"
                      maxLength={80}
                      className={FEEDBACK_INPUT_CLASS}
                    />
                  }
                />

                <SupportField
                  label={pageContent.feedback.fields.email}
                  input={
                    <input
                      type="email"
                      maxLength={80}
                      className={FEEDBACK_INPUT_CLASS}
                    />
                  }
                />

                <SupportField
                  label={pageContent.feedback.fields.phone}
                  input={
                    <PatternFormat
                      format="0 (###) ### ## ##"
                      mask="_"
                      className={FEEDBACK_INPUT_CLASS}
                    />
                  }
                />
              </div>

              <div className="mt-10 grid gap-4 1.5md:mt-12 lg:grid-cols-[130px_1fr] lg:items-start">
                <div className="pt-1 text-left text-[15px] font-semibold text-text_secondary 1.5xl:text-[17px] lg:text-right">
                  {pageContent.feedback.fields.department}
                </div>

                <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2 1.5md:gap-y-8 xl:grid-cols-4">
                  {pageContent.feedback.departments.map((department) => (
                    <label
                      key={department}
                      className="inline-flex items-center gap-2.5 text-[14px] text-text_secondary sm:text-[15px] 1.5xl:text-[16px]"
                    >
                      <input
                        type="checkbox"
                        className="h-5 w-5 shrink-0 rounded-none border border-[#aeb4be] text-bg_primary focus:ring-0"
                      />
                      <span>{department}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mt-10 1.5md:mt-12 lg:ps-[130px] xl:mt-14">
                <textarea
                  maxLength={500}
                  rows={7}
                  className={`${FEEDBACK_INPUT_CLASS} min-h-[12.5rem] resize-none px-4 py-4`}
                  placeholder={pageContent.feedback.fields.messagePlaceholder}
                />

                <div className="mt-4 text-[13px] leading-[1.7] text-text_secondary sm:text-[14px] 1.5xl:mt-5 1.5xl:text-[16px] 1.5xl:leading-8">
                  <p>{pageContent.feedback.noteTitle}</p>
                  <p>{pageContent.feedback.noteText}</p>
                </div>

                <div className="mt-7 flex justify-center sm:justify-end 1.5xl:mt-10">
                  <Button type="submit">
                    {pageContent.feedback.submit}
                  </Button>
                </div>
              </div>
            </form>
          </AnimatedComponent>
        </div>
      </section>

      <section className="bg-bg_primary py-12 sm:py-14 1.5md:py-16">
        <div className="container mx-auto max-w-8xl px-4">
          <AnimatedComponent
            direction="animate-fade-up animate-duration-500"
            className="mx-auto max-w-[55rem] 1.5xl:max-w-[73rem]"
          >
            <div className="1.5xl:flex 1.5xl:items-center 1.5xl:justify-between 1.5xl:gap-12">
              <h2 className="text-[26px] font-bold leading-tight text-text_primary sm:text-[30px] 1.5md:text-[34px]">
                {pageContent.formsSection.title}
              </h2>

              <div className="mt-8 grid gap-5 sm:mt-9 sm:grid-cols-2 sm:gap-8 1.5md:mt-10 1.5xl:mt-0 1.5xl:flex 1.5xl:items-center 1.5xl:gap-12">
                {pageContent.formsSection.items.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    download={item.download}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-3 text-left"
                  >
                    <img
                      src="/images/support/anasayfa_formlar_icon.svg"
                      alt=""
                      className="h-[3rem] w-auto shrink-0 transition-transform duration-200 group-hover:translate-y-[-1px]"
                    />
                    <span className="text-[15px] font-semibold leading-snug text-white sm:text-[16px]">
                      {item.label}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </AnimatedComponent>
        </div>
      </section>
    </>
  );
}

function SupportField({ label, input }) {
  return (
    <div className="grid gap-2 1.5md:gap-3 lg:grid-cols-[130px_1fr] lg:items-center">
      <div className="text-left text-[15px] font-semibold text-text_secondary sm:text-[16px] 1.5xl:text-[17px] lg:text-right">
        {label}
      </div>
      <div>{input}</div>
    </div>
  );
}
